import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleOAuthCallback } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export const AuthCallbackPage: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();

  useEffect(() => {
    async function processCallback() {
      try {
        // Get session from Supabase OAuth callback
        const { accessToken, refreshToken, error: oauthError } = await handleOAuthCallback();

        if (oauthError) {
          setError(oauthError.message);
          return;
        }

        if (!accessToken || !refreshToken) {
          setError('Failed to get session from OAuth provider');
          return;
        }

        // Store tokens in localStorage (same format as regular login)
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);

        // Refresh auth state to load user profile
        await refreshAuth();

        // Navigate to home page
        navigate('/', { replace: true });
      } catch (e) {
        console.error('OAuth callback error:', e);
        setError(e instanceof Error ? e.message : 'An unexpected error occurred');
      }
    }

    processCallback();
  }, [navigate, refreshAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-destructive/10 border border-destructive rounded-lg p-6">
            <h2 className="text-lg font-semibold text-destructive mb-2">
              Authentication Failed
            </h2>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};
